import { describirAdscripcion } from '@shared/adscripcion';
import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  input,
  model,
  signal,
  effect,
} from '@angular/core';
import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import {
  SolicitudVacaciones,
  SaldoVacaciones,
  PeriodoVacaciones,
} from '@schemas/vacaciones.schema';
import { VacacionesService } from '@services/vacaciones.service';
import {
  getEstatusTono,
  getResultadoTexto,
  getResultadoTipo,
  getResultadoMotivo,
  getPasosFlujo,
  type PasoFlujo,
  diasDescontadosDelSaldo,
  incumplePoliticaAnticipacion,
} from '@shared/vacaciones.helpers';

export type DetalleModalMode = 'director' | 'rh' | 'admin';

import { DetailField } from '../detail-field/detail-field';

import { PageHeader, SectionHeader, StatusText } from '@intelishs-group/ui';
import { leerSaldo } from '@shared/vacaciones.utils';

@Component({
  selector: 'app-solicitud-detalle-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DetailField,
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
    NzModalModule,
    NzIconModule,
    NzSpinModule,
    PageHeader,
    SectionHeader,
    StatusText,
  ],
  templateUrl: './solicitud-detalle-modal.html',
  styleUrl: './solicitud-detalle-modal.css',
})
export class SolicitudDetalleModal {
  private readonly vacacionesService = inject(VacacionesService);

  visible = model(false);
  solicitud = input<SolicitudVacaciones | null>(null);
  modo = input<DetalleModalMode>('director');
  /** Cuando true oculta la sección de detalle de solicitud y solo muestra saldo (solo modo director). */
  soloSaldo = input(false);

  saldo = signal<SaldoVacaciones | null>(null);
  /** Deuda real, comprometido a futuro o disponible. Ver `leerSaldo`. */
  lecturaSaldo = computed(() => {
    const s = this.saldo();
    return s ? leerSaldo(s) : null;
  });
  loadingSaldo = signal(false);

  getEstatusTono = getEstatusTono;
  describirAdscripcion = describirAdscripcion;
  getResultadoTexto = getResultadoTexto;
  getResultadoTipo = getResultadoTipo;
  getResultadoMotivo = getResultadoMotivo;
  incumplePoliticaAnticipacion = incumplePoliticaAnticipacion;

  /**
   * Los pasos del flujo, del helper que comparte con el rastreador del
   * empleado. Este modal pinta además `nombre` y `comentario`, y omite el
   * `detalle` en segunda persona: a RH y al director les informa, no les
   * acompaña.
   */
  pasos(sol: SolicitudVacaciones): PasoFlujo[] {
    return getPasosFlujo(sol);
  }

  /**
   * Días de esta solicitud que el saldo mostrado ya trae descontados. Cero en
   * `soloSaldo`, donde no hay solicitud de por medio y la nota no aplicaría.
   */
  readonly diasDescontados = computed(() =>
    this.soloSaldo() ? 0 : diasDescontadosDelSaldo(this.solicitud()),
  );

  constructor() {
    // Cargar saldo cuando se abre el modal y hay solicitud.
    effect(() => {
      if (!this.visible()) return;
      const sol = this.solicitud();
      if (!sol || !sol.empleadoId) {
        this.saldo.set(null);
        return;
      }
      this.saldo.set(null);
      this.loadingSaldo.set(true);
      this.vacacionesService.getSaldo(sol.empleadoId).subscribe({
        next: (s) => {
          this.saldo.set(s);
          this.loadingSaldo.set(false);
        },
        error: () => this.loadingSaldo.set(false),
      });
    });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  // ── Helpers de periodos (solo usados en modo 'director') ─────────
  /**
   * El ciclo anterior VIGENTE, le queden días o no. Antes exigía días
   * disponibles, y como el saldo ya descuenta esta solicitud (las pendientes
   * consumen), cuando ella agotaba el ciclo viejo la tarjeta desaparecía y el
   * director solo veía el ciclo actual: parecía que los días salían de ahí.
   */
  getPeriodoAnterior(periodos: PeriodoVacaciones[]): PeriodoVacaciones | null {
    return periodos.find((p) => !p.enCurso && !p.vencido) ?? null;
  }

  getPeriodoAnteriorVencido(periodos: PeriodoVacaciones[]): PeriodoVacaciones | null {
    if (this.getPeriodoAnterior(periodos)) return null;
    return (
      periodos.filter((p) => !p.enCurso && p.vencido).sort((a, b) => b.numero - a.numero)[0] ?? null
    );
  }

  getPeriodoActual(periodos: PeriodoVacaciones[]): PeriodoVacaciones | null {
    return periodos.find((p) => p.enCurso) ?? null;
  }
}
